import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { useAuth } from '../hooks/useAuth';
import { ResponsiveUserLayout } from '../components/ResponsiveUserLayout';
import { ResponsiveAdminLayout } from '../components/ResponsiveAdminLayout';

export const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  const isAdmin = user?.userType === 'admin' || user?.userType === 'superadmin';

  const renderContent = () => (
    <>
      {/* モバイル用ヘッダー */}
      {!isDesktop && (
        <AppBar
          position="static"
          sx={{
            bgcolor: 'white',
            boxShadow: 'none',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} sx={{ color: 'black' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, color: 'black', fontWeight: 600 }}
            >
              ヘルプ
            </Typography>
          </Toolbar>
        </AppBar>
      )}
      {/* メインコンテンツ */}
      <Box sx={{ 
        px: 2,
        pb: 2,
        pt: isAdmin && isDesktop ? 0 : 0,
        // 設定画面のプライバシー見出しと同じ上余白に合わせる（ユーザー表示時は常に mt:2）
        mt: isAdmin ? (isDesktop ? 0 : 2) : 2,
      }}>
        {/* サポートセンターセクション */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
              color: AppColors.primary,
              mb: 1,
            }}
          >
            サポートセンター
          </Typography>
          <Card sx={{ boxShadow: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <EmailIcon sx={{ color: AppColors.primary, fontSize: 24, mr: 1.5, mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    メールサポート
                  </Typography>
                  <Link
                    href="mailto:info@lefria.com"
                    sx={{
                      fontSize: 14,
                      color: '#666',
                      mb: 1,
                      textDecoration: 'none',
                      display: 'inline-block',
                      cursor: 'pointer',
                      '&:hover': {
                        color: AppColors.primary,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    info@lefria.com
                  </Link>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: 'rgba(0, 0, 0, 0.87)',
                      lineHeight: 1.4,
                    }}
                  >
                    アプリの使い方や技術的な問題でお困りの際は、上記のメールアドレスまでお気軽にお問い合わせください。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* よくある質問セクション */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
              color: AppColors.primary,
              mb: 1,
            }}
          >
            よくある質問
          </Typography>
          <Card sx={{ boxShadow: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <HelpIcon sx={{ color: AppColors.primary, fontSize: 22, mr: 1.5, mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    アプリが起動しません
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: 'rgba(0, 0, 0, 0.87)',
                      lineHeight: 1.4,
                    }}
                  >
                    アプリを再起動してください。問題が解決しない場合は、サポートセンターにお問い合わせください。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* サポート情報セクション */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
              color: AppColors.primary,
              mb: 1,
            }}
          >
            サポート情報
          </Typography>
          <Card sx={{ boxShadow: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <ScheduleIcon sx={{ color: AppColors.primary, fontSize: 24, mr: 1.5, mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    対応時間
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: '#666',
                      mb: 1,
                    }}
                  >
                    24時間対応
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: 'rgba(0, 0, 0, 0.87)',
                      lineHeight: 1.4,
                    }}
                  >
                    メールサポートは24時間受付しております。お問い合わせいただいた内容について、丁寧にサポートいたします。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );

  if (isAdmin && isDesktop) {
    return (
      <ResponsiveAdminLayout pageTitle="ヘルプ">
        <Box>
          {/* 基準の余白の状態: PCは最大幅1000px・中央寄せ */}
          <Box sx={{ 
            flex: 1,
            px: 2,
            pt: 2,
            pb: 0,
            maxWidth: '1000px',
            mx: 'auto',
            width: '100%',
          }}>
            {renderContent()}
          </Box>
        </Box>
      </ResponsiveAdminLayout>
    );
  }

  return (
    <ResponsiveUserLayout 
      maxWidth="1000px"
      topFullWidth={isDesktop ? (
        <Box
          sx={{
            width: '100%',
            p: 2,
            bgcolor: 'white',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: 'black',
            }}
          >
            ヘルプ
          </Typography>
        </Box>
      ) : null}
    >
      {renderContent()}
    </ResponsiveUserLayout>
  );
};
